import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Mail, FileText, Paperclip, AlertCircle, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const categoryColors = {
    tasks: 'bg-blue-500',
    ideas: 'bg-purple-500',
    waiting: 'bg-yellow-500',
    schedule: 'bg-green-500',
    advertising: 'bg-red-500',
    newsletters: 'bg-indigo-500',
    personal: 'bg-pink-500',
    unclassified: 'bg-gray-500',
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your Gmail automation</p>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Emails</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_emails || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.unprocessed_emails || 0} unprocessed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Invoices</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_invoices || 0}</div>
            <p className="text-xs text-muted-foreground">Total invoices found</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Attachments</CardTitle>
            <Paperclip className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_attachments || 0}</div>
            <p className="text-xs text-muted-foreground">Files downloaded</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spending</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${(stats?.total_spending || 0).toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">From invoices</p>
          </CardContent>
        </Card>
      </div>

      {/* Email Categories */}
      <Card>
        <CardHeader>
          <CardTitle>Emails by Category</CardTitle>
          <CardDescription>Distribution of classified emails</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {stats?.emails_by_category && Object.entries(stats.emails_by_category).map(([category, count]) => (
              <div key={category} className="flex items-center">
                <div className="flex-1 flex items-center gap-2">
                  <div className={`h-3 w-3 rounded-full ${categoryColors[category] || 'bg-gray-500'}`} />
                  <span className="text-sm font-medium capitalize">{category.replace('_', ' ')}</span>
                </div>
                <span className="text-sm text-muted-foreground">{count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Invoice Categories */}
      {stats?.total_invoices > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Spending by Category</CardTitle>
            <CardDescription>Invoice amounts by category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.spending_by_category && Object.entries(stats.spending_by_category)
                .filter(([_, amount]) => amount > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([category, amount]) => (
                  <div key={category} className="flex items-center">
                    <div className="flex-1">
                      <span className="text-sm font-medium capitalize">
                        {category.replace('_', ' ')}
                      </span>
                    </div>
                    <span className="text-sm font-bold">${amount.toFixed(2)}</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {stats?.last_sync && (
        <p className="text-xs text-muted-foreground">
          Last sync: {new Date(stats.last_sync).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default Dashboard;